import * as R from 'ramda';
import React, { useReducer, useCallback } from 'react';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature rate
import {
  mapDriversToOptions,
  mapEquipmentsToOptions,
  mapAvailableDriversToOptions,
  mapRateDataWithProperQuantity,
  mapAvailableTrailersToOptions,
} from '../helpers';
//////////////////////////////////////////////////

const initialState = {
  rateData: {},
  rateInfo: null,
  availTrucks: [],
  vendorRates: [],
  trucksOptions: [],
  availTrailers: [],
  trailersOptions: [],
  availableDrivers: {},
  primaryDriversOptions: [],
  secondaryDriversOptions: [],
};

function reducer(state, { type, payload }) {
  switch (type) {
    case 'UPDATE_STATE':
      return { ...state, ...payload };
    default:
      return state;
  }
}

const useFleetAssignment = ({
  openLoader,
  closeLoader,
  ignoreHazmat,
  shouldCheckHazmat,
  requestDriverRateData = sendRequest,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const ignoreHazmatEndorsement = R.not(shouldCheckHazmat);

  const updateState = (payload) => dispatch({ payload, type: 'UPDATE_STATE' });

  const getAvailableDrivers = async (reqBody) => {
    G.callFunctionWithArgs(openLoader, { showDimmer: true });

    const res = await requestDriverRateData('post', endpointsMap.availableDrivers, {
      data: { ignoreHazmatEndorsement, ...reqBody },
    });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const { drivers } = data;

      const teamDrivers = drivers.filter(R.prop(GC.FIELD_TEAM_DRIVER));

      updateState({
        availableDriversData: data,
        rateInfo: R.omit(['drivers'], data),
        secondaryDriversOptions: mapAvailableDriversToOptions(teamDrivers),
        primaryDriversOptions: mapAvailableDriversToOptions(drivers, true),
      });
    } else {
      G.handleFailResponseSimple(res);
    }

    G.callFunction(closeLoader);
  };

  const getDriverCompensations = async (reqBody) => {
    G.callFunctionWithArgs(openLoader, { showDimmer: true });

    const res = await requestDriverRateData('post', endpointsMap.driverCompensations, { data: reqBody });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const teamDrivers = data.rateData.filter(R.prop(GC.FIELD_TEAM_DRIVER));
      const forAssignDriverGuid = R.path(['rateData', '0', GC.FIELD_GUID], data);

      updateState({
        rateGeneratedGuid: G.generateGuid(),
        rateData: mapRateDataWithProperQuantity(data.rateData),
        secondaryDriversOptions: mapDriversToOptions(teamDrivers),
        primaryDriversOptions: mapDriversToOptions(data.rateData, true),
        rateInfo: R.assoc('forAssignDriverGuid', forAssignDriverGuid, R.omit(['rateData'], data)),
      });
    } else {
      G.handleFailResponseSimple(res, true, 'useFleetAssignment -> getDriverCompensations');
    }

    G.callFunction(closeLoader);
  };

  const getAvailTrucksByTelGuid = async (telGuid) => {
    const res = await requestDriverRateData(
      'get',
      endpointsMap.availableTrucks,
      G.setParamToRequestQuery(GC.FIELD_TEL_GUID, telGuid),
    );

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      updateState({
        availTrucks: data,
        trucksOptions: mapEquipmentsToOptions(data),
      });
    }
  };

  const getAvailTrailersByTelGuid = async (params) => {
    const { showAll } = params;

    if (showAll) G.callFunction(openLoader);

    const res = await requestDriverRateData('get', endpointsMap.availableTrailers, { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      updateState({
        availTrailers: data,
        trailersOptions: mapAvailableTrailersToOptions(data),
      });
    } else {
      G.handleFailResponseSimple(res);
    }

    if (showAll) G.callFunction(closeLoader);
  };

  const getAvailTrailersByBranchGuidAndTelEvents = async (reqBody) => {
    const { showAll } = reqBody;

    if (showAll) G.callFunction(openLoader);

    const res = await requestDriverRateData('post', endpointsMap.availableTrailers, { data: reqBody });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      updateState({
        availTrailers: data,
        trailersOptions: mapAvailableTrailersToOptions(data),
      });
    }

    if (showAll) G.callFunction(closeLoader);
  };

  const getAvailTrucksByBranchGuidAndTelEvents = async (reqBody) => {
    const res = await requestDriverRateData('post', endpointsMap.availableTrucks, { data: reqBody });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      updateState({
        availTrucks: data,
        trucksOptions: mapEquipmentsToOptions(data),
      });
    }
  };

  const getAssignmentByLoadData = useCallback((reqBody) => {
    const { telGuid, shouldGetCompensations } = reqBody;

    getAvailTrucksByTelGuid(telGuid);
    getAvailTrailersByTelGuid({ telGuid });

    if (G.isTrue(shouldGetCompensations)) return getDriverCompensations(reqBody);

    getAvailableDrivers(reqBody);
  }, []);

  const getAssignmentFromRouteBuilder = useCallback((reqBody) => {
    const { loadGuid, shouldGetCompensations, ignoreHazmatEndorsement } = reqBody;

    const branchGuid = R.prop(GC.FIELD_BRANCH_GUID, reqBody);

    const events = R.path(['loadData', 'events'], reqBody);

    let reqBodyWithEvents = {
      events,
      loadGuid,
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    };

    getAvailTrucksByBranchGuidAndTelEvents(reqBodyWithEvents);
    getAvailTrailersByBranchGuidAndTelEvents(reqBodyWithEvents);

    if (G.isTrue(shouldGetCompensations)) return getDriverCompensations(reqBody);

    if (G.isBoolean(ignoreHazmatEndorsement)) {
      reqBodyWithEvents = { ...reqBodyWithEvents, ignoreHazmatEndorsement };
    }

    getAvailableDrivers(reqBodyWithEvents);
  }, []);

  const getAssignmentFromOrder = useCallback((reqBody) => {
    const { shouldGetCompensations, ignoreHazmatEndorsement } = reqBody;

    const branchGuid = R.prop(GC.FIELD_BRANCH_GUID, reqBody);

    const events = R.path(['loadData', 'events'], reqBody);

    let reqBodyWithEvents = {
      events,
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    };

    getAvailTrucksByBranchGuidAndTelEvents(reqBodyWithEvents);
    getAvailTrailersByBranchGuidAndTelEvents(reqBodyWithEvents);

    if (G.isTrue(shouldGetCompensations)) return getDriverCompensations(reqBody);

    if (G.isBoolean(ignoreHazmatEndorsement)) {
      reqBodyWithEvents = { ...reqBodyWithEvents, ignoreHazmatEndorsement };
    }

    getAvailableDrivers(reqBodyWithEvents);
  }, []);

  const { availableDriversData, primaryDriversOptions, rateData: currentRateData } = state;

  const getSingleDriverCompensations = useCallback(async (reqBody) => {
    if (G.isNotNilAndNotEmpty(currentRateData)) return;

    G.callFunctionWithArgs(openLoader, { showDimmer: true});

    const res = await requestDriverRateData('post', endpointsMap.driverCompensations, { data: reqBody });

    const { data, status } = res;

    let returnData = null;

    if (G.isResponseSuccess(status)) {
      const rateData = R.prop('rateData', data);

      if (G.isNilOrEmpty(rateData)) {
        G.callFunction(closeLoader);

        return returnData;
      }

      const driver = R.head(rateData);

      returnData = driver;

      const currentAvailableDriversDataDrivers = R.pathOr([], [GC.FIELD_DRIVERS], availableDriversData);

      const mappedRateData = mapDriversToOptions(rateData, true);

      const option = R.head(mappedRateData);
      const optionValue = G.getValueFromObject(option);

      const newPrimaryDriverOptions = R.compose(
        R.map((item) => {
          if (R.propEq(optionValue, 'value', item)) return option;

          return item;
        }),
      )(R.or(primaryDriversOptions, []));

      const newAvailableDriversDataDrivers = R.compose(
        R.map((item) => {
          if (R.eqProps(GC.FIELD_GUID, driver, item)) return R.mergeRight(item, driver);

          return item;
        }),
      )(currentAvailableDriversDataDrivers);

      updateState({
        rateGeneratedGuid: G.generateGuid(),
        primaryDriversOptions: newPrimaryDriverOptions,
        availableDriversData: R.assoc('drivers', newAvailableDriversDataDrivers, availableDriversData),
      });
    } else {
      G.handleFailResponseSimple(res, true, 'useFleetAssignment -> getSingleDriverCompensations');
    }

    G.callFunction(closeLoader);

    return returnData;
  }, [currentRateData, availableDriversData, primaryDriversOptions]);

  const { vendorRates, availTrucks, trucksOptions } = state;

  const getVendorRates = useCallback(async (reqBody) => {
    const { truckGuids } = reqBody;

    const truckGuidsToUse = R.map(R.prop(GC.FIELD_GUID), availTrucks);
    const currentTruckVendorRatesGuids = R.map(R.prop(GC.FIELD_GUID), vendorRates);

    const guidsToUse = G.ifElse(
      G.isNilOrEmpty(truckGuids),
      R.without(currentTruckVendorRatesGuids, truckGuidsToUse),
      R.without(currentTruckVendorRatesGuids, R.or(truckGuids, [])),
    );

    if (G.isNilOrEmpty(guidsToUse)) {
      updateState({ vendorRateGeneratedGuid: G.generateGuid() });

      return null;
    }

    G.callFunctionWithArgs(openLoader, { showDimmer: true});

    const res = await requestDriverRateData(
      'post',
      endpointsMap.vendorRates,
      { data: R.assoc('truckGuids', guidsToUse, reqBody) },
    );

    const { data, status } = res;

    let returnData = null;

    if (G.isResponseSuccess(status)) {
      if (G.isNilOrEmpty(data)) {
        G.callFunction(closeLoader);

        return null;
      }

      const newVendorRates = R.concat(vendorRates, data);
      const indexedRates = R.indexBy(R.prop(GC.FIELD_GUID), data);

      returnData = newVendorRates;

      const newTrucksOptions = R.map((option) => {
        const { label, vendorGuid } = option;

        const vendorRate = R.prop(vendorGuid, indexedRates);

        if (G.isNilOrEmpty(vendorRate)) return option;

        const rateTotal = R.prop(GC.FIELD_TOTAL, vendorRate);

        const totalString = G.ifElse(
          G.isNotNilAndNotEmpty(rateTotal),
          `,
          ${G.getWindowLocale('titles:total-rate', 'Total Rate')}: $${rateTotal}`,
          '',
        );

        const newLabel = R.compose(
          R.head,
          R.split(','),
        )(label);

        return R.assoc('label', `${newLabel}${totalString}`, option);
      }, trucksOptions);

      updateState({
        vendorRates: newVendorRates,
        trucksOptions: newTrucksOptions,
        vendorRateGeneratedGuid: G.generateGuid(),
      });
    } else {
      G.handleFailResponseSimple(res, true, 'useFleetAssignment -> getVendorRates');
    }

    G.callFunction(closeLoader);

    return returnData;
  }, [vendorRates, availTrucks, trucksOptions]);

  return {
    state,
    getVendorRates,
    getAssignmentFromOrder,
    getAssignmentByLoadData,
    getAvailTrailersByTelGuid,
    getSingleDriverCompensations,
    getAssignmentFromRouteBuilder,
    getAvailTrailersByBranchGuidAndTelEvents,
  };
};

const optionsToPick = [
  'trucksOptions',
  'trailersOptions',
  'primaryDriversOptions',
  'secondaryDriversOptions',
];

const withFleetAssignment = (Component) => (props) => {
  const {
    state,
    getVendorRates,
    getAssignmentFromOrder,
    getAssignmentByLoadData,
    getAvailTrailersByTelGuid,
    getSingleDriverCompensations,
    getAssignmentFromRouteBuilder,
    getAvailTrailersByBranchGuidAndTelEvents,
  } = useFleetAssignment(props);

  const {
    rateData,
    rateInfo,
    availTrucks,
    vendorRates,
    availTrailers,
    rateGeneratedGuid,
    availableDriversData,
    vendorRateGeneratedGuid,
  } = state;

  return (
    <Component
      {...props}
      rateData={rateData}
      rateInfo={rateInfo}
      availTrucks={availTrucks}
      vendorRates={vendorRates}
      availTrailers={availTrailers}
      getVendorRates={getVendorRates}
      rateGeneratedGuid={rateGeneratedGuid}
      availableDriversData={availableDriversData}
      getAssignmentFromOrder={getAssignmentFromOrder}
      getAssignmentByLoadData={getAssignmentByLoadData}
      vendorRateGeneratedGuid={vendorRateGeneratedGuid}
      fleetOptionsForSelect={R.pick(optionsToPick, state)}
      getAvailTrailersByTelGuid={getAvailTrailersByTelGuid}
      getSingleDriverCompensations={getSingleDriverCompensations}
      getAssignmentFromRouteBuilder={getAssignmentFromRouteBuilder}
      getAvailTrailersByBranchGuidAndTelEvents={getAvailTrailersByBranchGuidAndTelEvents}
    />
  );
};

export {
  useFleetAssignment,
  withFleetAssignment,
};
